import { readingDiagnostic } from "./telemetry";
import { useEffect,useLayoutEffect,useMemo,useRef,useState,type ReactNode } from "react";
import { ActivityHeader } from "../../components/activity-header/ActivityHeader";
import { ChildIdentity,childThemeStyle } from "../../components/child-theme/ChildTheme";
import { createReadingApi,ReadingApiContext,useReadingApi,type ReadingActor } from "./api";
import { cancelBrowserSpeech } from "./browser-speech";
import { ComprehensionQuiz as Quiz } from "./ComprehensionQuiz";
import { navigate,pushStoryUrl,childPickerUrl,useLocation } from "../../lib/navigation";
import { Reader } from "./Reader";
import "./reading.scss";
import { ReadingHeader as StoryHeader } from "./ReadingHeader";
import { SavedTopicForm as TopicForm } from "./SavedTopicForm";
import type { Language,ReadingPreset,Story } from "./types";
import { Loader,scrollToTop } from "./ui";
import { useQuestions } from "./useQuestions";
function Activity({ vocabularyUrl, header, language }: { vocabularyUrl: string; header?: ReactNode; language: Language }) {
  const { fetch, actor } = useReadingApi();
  const session = { kid: actor.child ?? { id: "parent", name: "הורים" } };
  const [story, setStory] = useState<Story | null>(null);
  const [view, setView] = useState<"reader" | "quiz">("reader");
  const polledQuestions = useQuestions(story?.storyId);
  const questions = story && !story.storyId ? { status: "failed" as const, items: [] } : polledQuestions;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const storyRequest = useRef(0);
  const storyAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    loadSharedStory();
    window.addEventListener("popstate", loadSharedStory);
    return () => {
      window.removeEventListener("popstate", loadSharedStory);
      storyAbort.current?.abort();
      storyRequest.current += 1;
      cancelBrowserSpeech();
    };
  }, []);

  useEffect(() => {
    const main = document.querySelector<HTMLElement>(".reading-root .main--reader");
    const footer = main?.querySelector<HTMLElement>(".reader-footer");
    if (!main || !footer) return;

    // The explanation and viewport size can both change the fixed footer's height.
    const observer = new ResizeObserver(() => {
      main.style.setProperty("--reader-footer-height", `${footer.offsetHeight}px`);
      const target = main.querySelector(".finish-note") || main.querySelector(".word--active");
      target?.scrollIntoView({ block: "nearest" });
    });
    observer.observe(footer);
    return () => {
      observer.disconnect();
      main.style.removeProperty("--reader-footer-height");
    };
  }, [story, view, busy, questions.status]);

  // A shared link (?story=<id>) opens straight in the reader once this device knows who is
  // reading; without a kid the picker shows first, so audio and word tracking always have one.
  async function loadSharedStory() {
    storyAbort.current?.abort();
    const controller = new AbortController();
    storyAbort.current = controller;
    const request = ++storyRequest.current;
    const id = new URLSearchParams(window.location.search).get("story");
    setStory(null);
    setView("reader");
    setError("");
    scrollToTop();
    if (!id) {
      setBusy(false);
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(`/api/story?id=${encodeURIComponent(id)}`, { signal: controller.signal });
      const data = await response.json() as { language?: Language; text?: string };
      if (request !== storyRequest.current) return;
      if (!response.ok || !data.text || !data.language) throw new Error("story_not_found");
      const url = new URL(window.location.href);
      if (url.searchParams.get("language") !== data.language) {
        url.searchParams.set("language", data.language);
        navigate(url.pathname + url.search, true);
      }
      setStory({ text: data.text.trim(), topic: "", language: data.language, storyId: id });
    } catch {
      if (request !== storyRequest.current) return;
      setError("לא הצלחנו לפתוח את הקישור. כדאי לבדוק שהקישור שלם ולנסות שוב.");
    } finally {
      if (request === storyRequest.current) setBusy(false);
    }
  }

  function newStory() {
    storyAbort.current?.abort();
    if (actor.actor === "parent") { navigate(childPickerUrl("reading")); return; }
    storyRequest.current += 1;
    pushStoryUrl();
    scrollToTop();
    setBusy(false);
    setStory(null);
    setView("reader");
    setError("");
  }

  function openQuestions() {
    setView("quiz");
  }

  async function generate(
    topic: string,
    textLanguage: Language,
    preset: ReadingPreset,
  ) {
    storyAbort.current?.abort();
    const controller = new AbortController();
    storyAbort.current = controller;
    const request = ++storyRequest.current;
    setBusy(true);
    setError("");

    const started = Date.now();
    let requestId: string | undefined;
    let status = 0;
    try {
      const isEnglish = textLanguage === "en";
      const response = await fetch(
        isEnglish ? "/api/generate-english" : "/api/generate",
        {
          method: "POST",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            isEnglish ? { topic, preset } : { topic, difficulty: preset },
          ),
        },
      );
      requestId = response.headers.get("X-Reading-Request-Id") ?? undefined;
      status = response.status;
      const data = await response.json() as { error?: string; text?: string; storyId?: string };
      if (request !== storyRequest.current) return;
      if (response.status === 422 && data.error === "unsafe_topic") {
        throw new Error("unsafe_topic");
      }
      if (!response.ok || !data.text) throw new Error("generation_failed");
      pushStoryUrl(data.storyId);
      setView("reader");
      setStory({ text: data.text.trim(), topic, language: textLanguage, storyId: data.storyId });
    } catch (error) {
      if (request !== storyRequest.current || controller.signal.aborted) return;
      readingDiagnostic("reading_text_client_failed", { requestId, stage: "client_text", preset, status,
        elapsedMs: Date.now() - started, reason: status ? `http_${status}` : "network" });
      setError(
        (error as Error).message === "unsafe_topic"
          ? "הנושא הזה לא מתאים ליצירת טקסט. כדאי לבחור נושא אחר."
          : "לא הצלחנו להכין טקסט. כדאי לבדוק את החיבור ולנסות שוב.",
      );
    } finally {
      if (request === storyRequest.current) setBusy(false);
    }
  }

  function screen() {
    if (busy) return <Loader />;
    if (story && view === "quiz" && questions.status === "ready") {
      return (
        <Quiz
          text={story.text}
          language={story.language}
          questions={questions.items}
          onNewStory={newStory}
        />
      );
    }
    if (story) {
      return (
        <Reader
          {...story}
          questions={questions}
          onOpenQuestions={openQuestions}
          onNewStory={newStory}
        />
      );
    }

    if (actor.actor === "parent") return <div className="reading-parent-entry activity-notice__card"><button className="generate-button" onClick={newStory}>בחירת ילד לקריאה</button></div>;
    return (
      <>
        <TopicForm
          key={session.kid.id}
          onGenerate={generate}
          busy={busy}
          language={language}
        />
      </>
    );
  }


  return (
    <div className={`app-shell ${story?.storyId && !busy && session?.kid ? "app-shell--with-header" : ""}`}>
      {header ?? <ActivityHeader activity="reading" otherUrl={vocabularyUrl} identity={<ChildIdentity name={session.kid.name} emoji={actor.child?.emoji} />}>
        {actor.actor === "parent" && <button type="button" onClick={() => navigate(childPickerUrl("reading"))}>בחירת ילד לקריאה</button>}
      </ActivityHeader>}
      <main id="main-content"
        className={`main ${!session || busy ? "main--loading" : story && session.kid ? "main--reader" : ""}`}
      >
        {story?.storyId && !busy && <StoryHeader key={story.storyId} storyId={story.storyId} />}
        {screen()}
        {error && <div className="error" role="alert">{error}</div>}
      </main>
    </div>
  );
}


export function ReadingActivity({ actor, vocabularyUrl, header }: { actor: ReadingActor; vocabularyUrl: string; header?: ReactNode }) {
  const location = useLocation();
  const language = new URLSearchParams(location.split("?")[1]).get("language") === "en" ? "en" : "he";
  const [changed, setChanged] = useState(false);
  const api = useMemo(() => createReadingApi(actor, () => setChanged(true)), [actor.actor, actor.familyId, actor.child?.id]);
  const contextApi = { ...api, actor };
  useLayoutEffect(() => { api.activate(); return () => { api.dispose(); cancelBrowserSpeech(); }; }, [api]);
  return <div className={`reading-root sentry-block${actor.child ? " child-theme" : ""}`} style={actor.child ? childThemeStyle(actor.child) : undefined} data-sentry-block dir="rtl">
    {changed ? <main className="activity-notice" role="alert"><div className="activity-notice__card"><p>הקורא השתנה בלשונית אחרת.</p><button onClick={() => window.location.reload()}>טעינה מחדש</button></div></main>
      : <ReadingApiContext.Provider value={contextApi}><Activity key={language} language={language} vocabularyUrl={vocabularyUrl} header={header} /></ReadingApiContext.Provider>}
  </div>;
}
